import { parseDashboardPrimarySnapshot } from '@/lib/dashboard/readiness/validation'
import type { DashboardPrimarySnapshot, DashboardScope } from '@/lib/dashboard/readiness/types'

export const DASHBOARD_CACHE_VERSION = 2
export const DASHBOARD_CACHE_MAX_AGE_MS = 15 * 60 * 1000
export const DASHBOARD_CACHE_MAX_BYTES = 256 * 1024
const CACHE_PREFIX = 'npfc:dashboard-primary'

export interface DashboardSnapshotCacheEntry {
  cacheVersion: typeof DASHBOARD_CACHE_VERSION
  storedAt: string
  snapshot: DashboardPrimarySnapshot
}

export interface DashboardSessionStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  readonly length?: number
  key?(index: number): string | null
}

function encodePart(value: string | number | null): string {
  return encodeURIComponent(value === null ? 'none' : String(value))
}

function utf8ByteLength(value: string): number {
  let bytes = 0
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0
    bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4
  }
  return bytes
}

export function dashboardSnapshotCacheKey(scope: DashboardScope): string {
  return [
    CACHE_PREFIX,
    encodePart(scope.userId),
    encodePart(scope.schemaVersion),
    encodePart(scope.seasonId),
    encodePart(scope.instanceId),
    encodePart(scope.gameweekId),
    encodePart(scope.activeLobbyId)
  ].join(':')
}

export function dashboardScopeIdentity(scope: DashboardScope): string {
  return [
    'dashboard-runtime',
    encodePart(scope.schemaVersion),
    encodePart(scope.userId),
    encodePart(scope.seasonId),
    encodePart(scope.instanceId),
    encodePart(scope.gameweekId),
    encodePart(scope.gameweekNumber),
    encodePart(scope.playMode),
    encodePart(scope.activeLobbyId)
  ].join(':')
}

export function dashboardScopesEqual(left: DashboardScope, right: DashboardScope): boolean {
  return (
    left.schemaVersion === right.schemaVersion &&
    left.userId === right.userId &&
    left.seasonId === right.seasonId &&
    left.instanceId === right.instanceId &&
    left.gameweekId === right.gameweekId &&
    left.gameweekNumber === right.gameweekNumber &&
    left.playMode === right.playMode &&
    left.activeLobbyId === right.activeLobbyId
  )
}

export function readDashboardSnapshotCache(params: {
  store: DashboardSessionStore
  scope: DashboardScope
  now?: number
}): DashboardSnapshotCacheEntry | null {
  const key = dashboardSnapshotCacheKey(params.scope)
  const raw = params.store.getItem(key)
  if (!raw) return null

  try {
    if (utf8ByteLength(raw) > DASHBOARD_CACHE_MAX_BYTES) {
      throw new Error('cache_too_large')
    }
    const value = JSON.parse(raw) as Partial<DashboardSnapshotCacheEntry>
    if (value.cacheVersion !== DASHBOARD_CACHE_VERSION || typeof value.storedAt !== 'string') {
      throw new Error('cache_version_invalid')
    }
    const storedAt = Date.parse(value.storedAt)
    const now = params.now ?? Date.now()
    if (
      !Number.isFinite(storedAt) ||
      storedAt > now ||
      now - storedAt > DASHBOARD_CACHE_MAX_AGE_MS
    ) {
      throw new Error('cache_expired')
    }
    const snapshot = parseDashboardPrimarySnapshot(value.snapshot)
    if (!dashboardScopesEqual(snapshot.scope, params.scope)) throw new Error('cache_scope_mismatch')
    return { cacheVersion: DASHBOARD_CACHE_VERSION, storedAt: value.storedAt, snapshot }
  } catch {
    params.store.removeItem(key)
    return null
  }
}

export function writeDashboardSnapshotCache(params: {
  store: DashboardSessionStore
  snapshot: DashboardPrimarySnapshot
  now?: number
}): boolean {
  const snapshot = parseDashboardPrimarySnapshot(params.snapshot)
  const entry: DashboardSnapshotCacheEntry = {
    cacheVersion: DASHBOARD_CACHE_VERSION,
    storedAt: new Date(params.now ?? Date.now()).toISOString(),
    snapshot
  }
  const serialized = JSON.stringify(entry)
  if (utf8ByteLength(serialized) > DASHBOARD_CACHE_MAX_BYTES) return false
  params.store.setItem(dashboardSnapshotCacheKey(snapshot.scope), serialized)
  return true
}

export function clearDashboardSnapshotCacheForUser(
  store: DashboardSessionStore,
  userId: string
): void {
  if (typeof store.length !== 'number' || !store.key) return
  const userMarker = `${CACHE_PREFIX}:${encodePart(userId)}:`
  const keys: string[] = []
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index)
    if (key?.startsWith(userMarker)) keys.push(key)
  }
  keys.forEach((key) => store.removeItem(key))
}
